import type { BusinessOrderExecution } from '~/types/business-order-execution'
import { getBusinessOrderExecutionKey } from '~/utils/formatBusinessOrder'

export const ACTIVE_EXECUTIONS_QUERY_KEY = ['business-order-executions', 'active'] as const

export type BusinessOrderExecutionPatch = Partial<BusinessOrderExecution> & {
    id?: number | string
    documentId?: string
}

type ActiveExecutionsCache = {
    getQueryData: (key: typeof ACTIVE_EXECUTIONS_QUERY_KEY) => unknown
    setQueryData: (key: typeof ACTIVE_EXECUTIONS_QUERY_KEY, data: BusinessOrderExecution[]) => unknown
}

export function isSameExecution(
    execution: { documentId?: string; id?: string | number },
    patch: { documentId?: string; id?: string | number },
): boolean {
    if (execution.documentId && patch.documentId && execution.documentId === patch.documentId) {
        return true
    }
    if (execution.id != null && patch.id != null && String(execution.id) === String(patch.id)) {
        return true
    }
    return false
}

export function applyExecutionPatch(
    list: BusinessOrderExecution[],
    patch: BusinessOrderExecutionPatch,
): BusinessOrderExecution[] {
    return list.map((item) => {
        if (!isSameExecution(item, patch)) {
            return item
        }
        return {
            ...item,
            ...patch,
            order: item.order,
        }
    })
}

export function applyExecutionUnreadCount(
    list: BusinessOrderExecution[],
    executionId: string,
    count: number,
): BusinessOrderExecution[] {
    return list.map((item) => {
        if (getBusinessOrderExecutionKey(item) !== executionId) {
            return item
        }
        if (item.unreadMessagesCount === count) {
            return item
        }
        return {
            ...item,
            unreadMessagesCount: count,
        }
    })
}

function readActiveExecutions(queryCache: ActiveExecutionsCache): BusinessOrderExecution[] | null {
    const list = queryCache.getQueryData(ACTIVE_EXECUTIONS_QUERY_KEY)
    if (!Array.isArray(list) || !list.length) {
        return null
    }
    return list as BusinessOrderExecution[]
}

function replaceActiveExecutions(
    queryCache: ActiveExecutionsCache,
    list: BusinessOrderExecution[],
    next: BusinessOrderExecution[],
) {
    if (next.every((item, index) => item === list[index])) {
        return
    }
    queryCache.setQueryData(ACTIVE_EXECUTIONS_QUERY_KEY, next)
}

export function patchActiveExecutionInCache(
    queryCache: ActiveExecutionsCache,
    patch: BusinessOrderExecutionPatch,
) {
    const list = readActiveExecutions(queryCache)
    if (!list) {
        return
    }
    replaceActiveExecutions(queryCache, list, applyExecutionPatch(list, patch))
}

export function setActiveExecutionUnreadCountInCache(
    queryCache: ActiveExecutionsCache,
    executionId: string,
    count: number,
) {
    if (!executionId) {
        return
    }
    const list = readActiveExecutions(queryCache)
    if (!list) {
        return
    }
    replaceActiveExecutions(queryCache, list, applyExecutionUnreadCount(list, executionId, count))
}
